//@ts-nocheck
import { useCallback, useMemo } from 'react';
import { MapPin, Plus, User } from 'lucide-react';
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from '@/components/ui/resizable';
import { Ticket } from '@/types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@radix-ui/react-tabs';
import { useMobile } from '@/hooks/useMobile';
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import TicketList from '@/components/shared/TicketList';
import { MapContainer } from '@/components/map/components/MapContainer';
import { useTickets } from '@/hooks/api/useTickets';
import { Search } from 'lucide-react';
import { useTicketFilters } from '@/hooks/useTicketFilters';
import { useDialogManager } from '@/hooks/useDialogManager';
import { useTabNavigation } from '@/hooks/useTabNavigation';
import { TicketQuickFilters } from '@/components/shared/TicketQuickFilters';
import debounce from 'lodash/debounce';

export const AdminMapView = () => {
  const { isMobile } = useMobile();
  const { openDialog } = useDialogManager();
  const { filters, setFilters } = useTicketFilters();

  const [activeTab, setActiveTab] = useTabNavigation('tab', 'tickets');

  const handleOpenCreateTicket = useCallback(() => {
    openDialog('create-ticket');
  }, []);

  const handleOpenEditTicket = useCallback((ticket: Ticket) => {
    openDialog('edit-ticket', ticket.id);
  }, []);

  const {
    data: tickets,
    total,
    totalPages,
    hasNext,
    hasPrev,
    isFetching,
    isLoading,
  } = useTickets();

  const debouncedSearch = useMemo(
    () => debounce((q: string) => setFilters({ q }), 500),
    [setFilters],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedSearch(e.target.value);
    },
    [debouncedSearch],
  );

  if (isLoading) return <Spinner />;

  return (
    <div className="h-full">
      {isMobile ? (
        <div className="w-full h-full flex flex-col overflow-hidden">
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full flex flex-col flex-1 min-h-0"
          >
            <TabsList className="grid w-full grid-cols-2 rounded-none flex-shrink-0">
              <TabsTrigger
                value="tickets"
                className="py-3 text-sm font-medium flex items-center justify-center gap-2 rounded-none data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-border text-foreground"
              >
                <User className="w-4 h-4" /> Tickets
              </TabsTrigger>
              <TabsTrigger
                value="map"
                className="py-3 text-sm font-medium flex items-center justify-center gap-2 rounded-none data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-border text-foreground"
              >
                <MapPin className="w-4 h-4" /> Map
              </TabsTrigger>
            </TabsList>

            <TabsContent value="tickets" className="flex-1 overflow-y-auto">
              <div className="p-4 border-b flex flex-col gap-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-foreground">
                    Tickets
                  </h2>
                  <Button
                    className="bg-primary dark:bg-secondary text-secondary-foreground hover:bg-secondary/90"
                    size="sm"
                    onClick={handleOpenCreateTicket}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create Ticket
                  </Button>
                </div>
                <SearchBar
                  defaultValue={filters.q}
                  onChange={handleSearchChange}
                  placeholder="Search by name or ID..."
                  className="w-full"
                />
                <TicketQuickFilters />
              </div>
              <div className="flex-1 overflow-y-auto">
                <TicketList
                  tickets={tickets}
                  total={total}
                  totalPages={totalPages}
                  hasNext={hasNext}
                  hasPrev={hasPrev}
                  isFetching={isFetching}
                />
              </div>
            </TabsContent>

            <TabsContent value="map" className="flex-1">
              <MapContainer tickets={tickets} selectedTicket={null} />
            </TabsContent>
          </Tabs>
        </div>
      ) : (
        <ResizablePanelGroup direction="horizontal" className="h-full">
          <ResizablePanel
            defaultSize={30}
            minSize={25}
            maxSize={70}
            className="border-r"
          >
            <div className="h-full flex flex-col">
              <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border p-4">
                <h2 className="text-xl font-semibold text-foreground">
                  Tickets
                </h2>
                <Button
                  className="bg-primary dark:bg-secondary text-secondary-foreground hover:bg-secondary/90"
                  size="sm"
                  onClick={handleOpenCreateTicket}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create Ticket
                </Button>
                <SearchBar
                  defaultValue={filters.q}
                  onChange={handleSearchChange}
                  placeholder="Search by name or ID..."
                />
                <TicketQuickFilters />
              </div>
              <div className="flex-1 overflow-y-auto">
                <TicketList
                  tickets={tickets}
                  total={total}
                  totalPages={totalPages}
                  hasNext={hasNext}
                  hasPrev={hasPrev}
                  isFetching={isFetching}
                />
              </div>
            </div>
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={70}>
            <MapContainer
              selectedTicket={null}
              onViewDetails={handleOpenEditTicket}
            />
          </ResizablePanel>
        </ResizablePanelGroup>
      )}
    </div>
  );
};

export const SearchBar = ({
  defaultValue,
  onChange,
  placeholder = 'Search...',
  className = '',
}: {
  defaultValue?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  className?: string;
}) => (
  <div className={`relative w-full ${className}`}>
    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
      <Search className="h-4 w-4 text-muted-foreground" />
    </div>
    <input
      type="text"
      defaultValue={defaultValue}
      onChange={onChange}
      placeholder={placeholder}
      className="block w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-border focus:outline-none focus:ring-1 focus:ring-ring"
    />
  </div>
);
